// ✅ src/pages/OrderManagement/ProjectCosting.jsx  (FULL FILE - UPDATED WITH LOCALSTORAGE HELPER + COMMON HEADERS)

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import ProjectPostOrderLayoutWrapper from "../../components/ProjectPostOrderLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaPlus, FaSave, FaChevronUp, FaChevronDown, FaEye } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/projectCosting";

const toNum = (v) => {
  if (v === null || v === undefined) return 0;
  const s = String(v).replace(/Rs\./gi, "").replace(/,/g, "").trim();
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
};

const fmt2 = (v) =>
  toNum(v).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const toInt = (v, def = 0) => {
  const n = parseInt(String(v ?? "").trim(), 10);
  return Number.isFinite(n) ? n : def;
};

const toStr = (v) => String(v ?? "").trim();

const ProjectCosting = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const navigate = useNavigate();

  // ✅ safe localStorage helper
  const getLS = (key, fallback = "") => {
    try {
      if (typeof window === "undefined") return fallback;
      const val = localStorage.getItem(key);
      return val !== null && val !== undefined ? val : fallback;
    } catch (err) {
      return fallback;
    }
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");

  const branchIdLS = getLS("branchId") || getLS("branchid");
  const branchTypeLS = getLS("branchType") || getLS("branchtype");
  const userIdLS = getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  // ✅ parsed values
  const branchId = useMemo(() => toInt(branchIdLS, 0), [branchIdLS]);
  const branchType = useMemo(() => toStr(branchTypeLS || "HO"), [branchTypeLS]);
  const loginUserId = useMemo(() => toStr(userIdLS), [userIdLS]);

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": loginUserId,
    }),
    [finYearId, finYear, branchId, branchType, loginUserId]
  );

  // ✅ finance year
  const financeYear = useMemo(() => {
    if (typeof window === "undefined") return "";
    return String(sessionStorage.getItem("ddlfinaceyearselect") || "");
  }, []);

  // show/hide filters
  const [showFilters, setShowFilters] = useState(true);

  // dropdown + grid data
  const [projects, setProjects] = useState([]);
  const [rows, setRows] = useState([]);

  // loading states
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingGrid, setLoadingGrid] = useState(false);
  const [loadingValues, setLoadingValues] = useState(false);
  const [saving, setSaving] = useState(false);

  // edit mode flags
  const [mode, setMode] = useState("N"); // N=new, U=update
  const [projectCostingId, setProjectCostingId] = useState(0);

  // Close checkbox
  const [close, setClose] = useState(false);

  // ✅ form state
  const [form, setForm] = useState({
    contractId: "",
    estimatedCost: "",
    emd: "",
    asd: "",
    materialExpenses: "",
    dieselExpenses: "",
    vehicleExpenses: "",
    labourExpenses: "",
    billAmount: "",
    departmentRecovery: "",
    otherExpenses1: "",
    otherExpenses2: "",
    payrollExpenses: "",
    productionExpenses: "",
    otherExpenses: "",
    officeContribution: "",
    grossTotal: "",
    estProfitLoss: "",
    estProfitLossPercent: "",
    netProfitLoss: "",
    netProfitLossPercent: "",
  });

  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // ✅ report redirect helper
  const openReport = (viewType) => {
    if (!form.contractId) {
      toast.error("Please Select Project");
      return;
    }

    const pcProjId = Number(form.contractId);
    const pcBrId = Number(branchId);
    const selPcId = Number(projectCostingId || 0);

    sessionStorage.setItem("PCProjid", String(pcProjId));
    sessionStorage.setItem("PCBrid", String(pcBrId));
    sessionStorage.setItem("SelProjectCostingId", String(selPcId));
    sessionStorage.setItem("ViewType", String(viewType));

    const qs = `?pcProjId=${pcProjId}&pcBrId=${pcBrId}&selPcId=${selPcId}&viewType=${encodeURIComponent(
      viewType
    )}`;

    if (viewType === "Material") navigate(`/OrderManagement/MaterialExpensesReport${qs}`);
    else if (viewType === "Diesel") navigate(`/OrderManagement/DieselExpensesReport${qs}`);
    else if (viewType === "Payroll") navigate(`/OrderManagement/PayrollExpensesReport${qs}`);
  };

  // ---- totals calc ----
  const calcTotals = (x) => {
    const estimatedCost = toNum(x.estimatedCost);
    const emd = toNum(x.emd);
    const asd = toNum(x.asd);
    const materialExpenses = toNum(x.materialExpenses);
    const dieselExpenses = toNum(x.dieselExpenses);
    const vehicleExpenses = toNum(x.vehicleExpenses);
    const labourExpenses = toNum(x.labourExpenses);

    const departmentRecovery = toNum(x.departmentRecovery);
    const otherExpenses1 = toNum(x.otherExpenses1);
    const otherExpenses2 = toNum(x.otherExpenses2);

    const otherExpenses = toNum(x.otherExpenses);
    const productionExpenses = toNum(x.productionExpenses);
    const payrollExpenses = toNum(x.payrollExpenses);
    const officeContribution = toNum(x.officeContribution);

    const grossTotal =
      emd +
      asd +
      materialExpenses +
      vehicleExpenses +
      labourExpenses +
      departmentRecovery +
      otherExpenses1 +
      otherExpenses2 +
      otherExpenses +
      productionExpenses +
      dieselExpenses +
      payrollExpenses +
      officeContribution;

    const estProfitLoss = estimatedCost - grossTotal;
    const estProfitLossPercent = estimatedCost ? (estProfitLoss * 100) / estimatedCost : 0;

    const billAmount = toNum(x.billAmount);
    const netProfitLoss = billAmount - grossTotal;
    const netProfitLossPercent = billAmount ? (netProfitLoss * 100) / billAmount : 0;

    return {
      grossTotal,
      estProfitLoss,
      estProfitLossPercent,
      netProfitLoss,
      netProfitLossPercent,
    };
  };

  const recalc = (nextForm) => {
    const t = calcTotals(nextForm);
    return {
      ...nextForm,
      grossTotal: fmt2(t.grossTotal),
      estProfitLoss: fmt2(t.estProfitLoss),
      estProfitLossPercent: fmt2(t.estProfitLossPercent),
      netProfitLoss: fmt2(t.netProfitLoss),
      netProfitLossPercent: fmt2(t.netProfitLossPercent),
    };
  };

  const clearForm = () => {
    setMode("N");
    setProjectCostingId(0);
    setClose(false);
    setForm({
      contractId: "",
      estimatedCost: "",
      emd: "",
      asd: "",
      materialExpenses: "",
      dieselExpenses: "",
      vehicleExpenses: "",
      labourExpenses: "",
      billAmount: "",
      departmentRecovery: "",
      otherExpenses1: "",
      otherExpenses2: "",
      payrollExpenses: "",
      productionExpenses: "",
      otherExpenses: "",
      officeContribution: "",
      grossTotal: "",
      estProfitLoss: "",
      estProfitLossPercent: "",
      netProfitLoss: "",
      netProfitLossPercent: "",
    });
  };

  // ---- API calls ----
  const loadProjects = async () => {
    if (!branchId || !branchType) return;
    setLoadingProjects(true);
    try {
      const res = await axios.get(`${API_BASE}/projects`, {
        params: { branchId, branchType },
        headers: commonHeaders,
      });
      setProjects(res.data?.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Failed to load projects");
    } finally {
      setLoadingProjects(false);
    }
  };

  const loadGrid = async () => {
    if (!branchId) return;
    setLoadingGrid(true);
    try {
      const res = await axios.get(`${API_BASE}/list`, {
        params: { branchId },
        headers: commonHeaders,
      });
      setRows(res.data?.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Failed to load grid");
    } finally {
      setLoadingGrid(false);
    }
  };

  const loadValuesForProject = async (contractId) => {
    if (!contractId || !branchId) return;
    setLoadingValues(true);
    try {
      const res = await axios.get(`${API_BASE}/values`, {
        params: { contractId, branchId, financeYear },
        headers: commonHeaders,
      });

      const d = res.data?.data || {};

      const next = recalc({
        ...form,
        contractId: String(contractId),
        estimatedCost: fmt2(d.estimatedCost ?? 0),
        emd: fmt2(d.emd ?? 0),
        asd: fmt2(d.asd ?? 0),
        materialExpenses: fmt2(d.materialExpenses ?? 0),
        dieselExpenses: fmt2(d.dieselExpenses ?? 0),
        vehicleExpenses: fmt2(d.vehicleExpenses ?? 0),
        labourExpenses: fmt2(d.labourExpenses ?? 0),
        billAmount: fmt2(d.billAmount ?? 0),
        departmentRecovery: fmt2(d.departmentRecovery ?? 0),
        otherExpenses1: fmt2(d.otherExpenses1 ?? 0),
        otherExpenses2: fmt2(d.otherExpenses2 ?? 0),
        payrollExpenses: fmt2(d.payrollExpenses ?? 0),
        productionExpenses: fmt2(d.productionExpenses ?? 0),
        otherExpenses: fmt2(d.otherExpenses ?? 0),
        officeContribution: fmt2(d.officeContribution ?? 0),
      });

      setForm(next);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Failed to load project values");
    } finally {
      setLoadingValues(false);
    }
  };

  const viewSavedRow = async (pcId) => {
    if (!pcId) return;
    setLoadingValues(true);
    try {
      const res = await axios.get(`${API_BASE}/view/${pcId}`, {
        headers: commonHeaders,
      });

      const d = res.data?.data;
      if (!d) {
        toast.info("No data");
        return;
      }

      setMode("U");
      setProjectCostingId(Number(d.projectCostingId || pcId));
      setClose(Boolean(d.isClosed));

      const next = recalc({
        ...form,
        contractId: String(d.contractId || ""),
        estimatedCost: fmt2(d.estimatedCost ?? 0),
        emd: fmt2(d.emd ?? 0),
        asd: fmt2(d.asd ?? 0),
        materialExpenses: fmt2(d.materialExpenses ?? 0),
        dieselExpenses: fmt2(d.dieselExpenses ?? 0),
        vehicleExpenses: fmt2(d.vehicleExpenses ?? 0),
        labourExpenses: fmt2(d.labourExpenses ?? 0),
        billAmount: fmt2(d.billAmount ?? 0),
        departmentRecovery: fmt2(d.departmentRecovery ?? 0),
        otherExpenses1: fmt2(d.otherExpenses1 ?? 0),
        otherExpenses2: fmt2(d.otherExpenses2 ?? 0),
        payrollExpenses: fmt2(d.payrollExpenses ?? 0),
        productionExpenses: fmt2(d.productionExpenses ?? 0),
        otherExpenses: fmt2(d.otherExpenses ?? 0),
        officeContribution: fmt2(d.officeContribution ?? 0),
      });

      setForm(next);
      setShowFilters(true);
      toast.success("Loaded for edit");
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Failed to view");
    } finally {
      setLoadingValues(false);
    }
  };

  const handleChange = (field, value) => {
    const next = recalc({ ...form, [field]: value });
    setForm(next);
  };

  const save = async () => {
    if (saving) return;

    if (!form.contractId) {
      toast.error("Please select Project Name");
      return;
    }

    if (!close) {
      toast.error("Select Close");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        mode,
        projectCostingId: mode === "U" ? projectCostingId : undefined,
        contractId: Number(form.contractId),
        branchId,
        loginUserId,
        financeYear,
        close,
        estimatedCost: toNum(form.estimatedCost),
        emd: toNum(form.emd),
        asd: toNum(form.asd),
        materialExpenses: toNum(form.materialExpenses),
        dieselExpenses: toNum(form.dieselExpenses),
        vehicleExpenses: toNum(form.vehicleExpenses),
        labourExpenses: toNum(form.labourExpenses),
        billAmount: toNum(form.billAmount),
        departmentRecovery: toNum(form.departmentRecovery),
        otherExpenses1: toNum(form.otherExpenses1),
        otherExpenses2: toNum(form.otherExpenses2),
        payrollExpenses: toNum(form.payrollExpenses),
        productionExpenses: toNum(form.productionExpenses),
        otherExpenses: toNum(form.otherExpenses),
        officeContribution: toNum(form.officeContribution),
      };

      const res = await axios.post(`${API_BASE}/save`, payload, {
        headers: commonHeaders,
      });

      if (res.data?.success) {
        toast.success(res.data?.message || "Saved");
        await loadGrid();
        clearForm();
      } else {
        toast.error(res.data?.message || "Save failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    loadProjects();
    loadGrid();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ProjectPostOrderLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">PROJECT COSTING</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              PROFIT / LOSS
            </h1>

            <button
              onClick={() => setShowFilters((p) => !p)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                isDark
                  ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
              }`}
            >
              {showFilters ? <FaChevronUp /> : <FaChevronDown />}
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Project Name
                  </label>

                  <select
                    value={form.contractId}
                    onChange={(e) => {
                      const cid = e.target.value;
                      setMode("N");
                      setProjectCostingId(0);
                      setClose(false);
                      setF("contractId", cid);
                      if (cid) loadValuesForProject(cid);
                    }}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                    }`}
                  >
                    <option value="">Select</option>
                    {(projects || []).map((p) => (
                      <option key={p.contractId} value={p.contractId}>
                        {p.contractName}
                      </option>
                    ))}
                  </select>

                  {loadingProjects && <div className="text-xs mt-1 opacity-80">Loading projects…</div>}
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Estimated Cost
                  </label>
                  <input
                    type="text"
                    value={form.estimatedCost}
                    onChange={(e) => handleChange("estimatedCost", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    EMD
                  </label>
                  <input
                    type="text"
                    value={form.emd}
                    onChange={(e) => handleChange("emd", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    ASD
                  </label>
                  <input
                    type="text"
                    value={form.asd}
                    onChange={(e) => handleChange("asd", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label
                    onClick={() => openReport("Material")}
                    title="Open Material Expenses Report"
                    className={`block text-sm font-semibold mb-2 cursor-pointer hover:underline ${
                      isDark ? "text-blue-400" : "text-blue-600"
                    }`}
                  >
                    Material Expenses
                  </label>
                  <input
                    type="text"
                    value={form.materialExpenses}
                    onChange={(e) => handleChange("materialExpenses", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label
                    onClick={() => openReport("Diesel")}
                    title="Open Diesel Expenses Report"
                    className={`block text-sm font-semibold mb-2 cursor-pointer hover:underline ${
                      isDark ? "text-blue-400" : "text-blue-600"
                    }`}
                  >
                    Diesel Expenses
                  </label>
                  <input
                    type="text"
                    value={form.dieselExpenses}
                    onChange={(e) => handleChange("dieselExpenses", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Vehicle Expenses
                  </label>
                  <input
                    type="text"
                    value={form.vehicleExpenses}
                    onChange={(e) => handleChange("vehicleExpenses", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Labour Expenses
                  </label>
                  <input
                    type="text"
                    value={form.labourExpenses}
                    onChange={(e) => handleChange("labourExpenses", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Bill Amount
                  </label>
                  <input
                    type="text"
                    value={form.billAmount}
                    onChange={(e) => handleChange("billAmount", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Department Recovery
                  </label>
                  <input
                    type="text"
                    value={form.departmentRecovery}
                    onChange={(e) => handleChange("departmentRecovery", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Other Expenses 1
                  </label>
                  <input
                    type="text"
                    value={form.otherExpenses1}
                    onChange={(e) => handleChange("otherExpenses1", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Other Expenses 2
                  </label>
                  <input
                    type="text"
                    value={form.otherExpenses2}
                    onChange={(e) => handleChange("otherExpenses2", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label
                    onClick={() => openReport("Payroll")}
                    title="Open Payroll Expenses Report"
                    className={`block text-sm font-semibold mb-2 cursor-pointer hover:underline ${
                      isDark ? "text-blue-400" : "text-blue-600"
                    }`}
                  >
                    Payroll Expenses
                  </label>
                  <input
                    type="text"
                    value={form.payrollExpenses}
                    onChange={(e) => handleChange("payrollExpenses", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Production Expenses
                  </label>
                  <input
                    type="text"
                    value={form.productionExpenses}
                    onChange={(e) => handleChange("productionExpenses", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Other (Bank) Expenses
                  </label>
                  <input
                    type="text"
                    value={form.otherExpenses}
                    onChange={(e) => handleChange("otherExpenses", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                    Office Contribution
                  </label>
                  <input
                    type="text"
                    value={form.officeContribution}
                    onChange={(e) => handleChange("officeContribution", e.target.value)}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                {[
                  ["grossTotal", "Gross Total"],
                  ["estProfitLoss", "Est Profit/Loss"],
                  ["estProfitLossPercent", "Est Profit/Loss in %"],
                  ["netProfitLoss", "Net Profit/Loss"],
                  ["netProfitLossPercent", "Net Profit/Loss in %"],
                ].map(([k, label]) => (
                  <div key={k}>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      {label}
                    </label>
                    <input
                      type="text"
                      value={form[k]}
                      readOnly
                      className={`w-full px-4 py-2 border rounded-lg transition-all ${
                        isDark ? "bg-gray-800 border-gray-700 text-white" : "bg-gray-50 border-gray-300 text-gray-900"
                      }`}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="close"
                checked={close}
                onChange={(e) => setClose(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="close" className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                Close
              </label>

              {loadingValues && <span className="text-xs opacity-80 ml-3">Loading values…</span>}
            </div>

            <div className="flex items-center gap-4">
              <button
                onClick={() => {
                  clearForm();
                  toast.success("Add New");
                }}
                className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark ? `${buttonClass} ${hoverClass} text-white` : `${buttonClass} ${hoverClass} text-white`
                }`}
              >
                <FaPlus />
                Add New
              </button>

              <button
                onClick={save}
                disabled={saving}
                className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  saving
                    ? "bg-gray-400 text-white cursor-not-allowed"
                    : isDark
                    ? "bg-gray-600 hover:bg-gray-700 text-white"
                    : "bg-gray-500 hover:bg-gray-600 text-white"
                }`}
              >
                <FaSave />
                {saving ? "Saving..." : mode === "U" ? "Update" : "Save"}
              </button>
            </div>
          </div>

          <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">Slno</th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">Project Code</th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">Project Name</th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">Gross Expenses</th>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">Net Profit</th>
                    <th className="px-4 py-4 text-center font-semibold">View</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loadingGrid ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-6 text-center">
                        Loading…
                      </td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-6 text-center">
                        No records
                      </td>
                    </tr>
                  ) : (
                    rows.map((r) => (
                      <tr
                        key={r.projectCostingId ?? r.slno}
                        className={`transition-colors duration-200 ${
                          isDark ? "bg-gray-800 hover:bg-gray-750" : "bg-white hover:bg-gray-50"
                        }`}
                      >
                        <td className={`px-4 py-3 text-center border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                          {r.slno}
                        </td>
                        <td className={`px-4 py-3 text-center border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                          {r.projectCode}
                        </td>
                        <td className={`px-4 py-3 text-center border-r ${isDark ? "border-gray-700 text-blue-400" : "border-gray-200 text-blue-600"}`}>
                          {r.projectName}
                        </td>
                        <td className={`px-4 py-3 text-center border-r ${isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"}`}>
                          {r.grossExpenses}
                        </td>
                        <td
                          className={`px-4 py-3 text-center border-r ${
                            toNum(r.netProfit) < 0
                              ? isDark
                                ? "text-red-400"
                                : "text-red-600"
                              : isDark
                              ? "text-green-400"
                              : "text-green-600"
                          }`}
                        >
                          {r.netProfit}
                        </td>
                        <td className={`px-4 py-3 text-center ${isDark ? "text-white" : "text-gray-900"}`}>
                          <button
                            onClick={() => viewSavedRow(r.projectCostingId)}
                            className={`inline-flex items-center gap-2 px-3 py-1 rounded-lg text-xs font-semibold ${
                              isDark ? "bg-blue-700 hover:bg-blue-600 text-white" : "bg-blue-100 hover:bg-blue-200 text-blue-700"
                            }`}
                          >
                            <FaEye />
                            View
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </ProjectPostOrderLayoutWrapper>
  );
};

export default ProjectCosting;